import { Spinner } from '@frontend/components/common';
import { ICONS, Plus } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { UserSelectV2 } from '@frontend/user/select';
import { User } from '@frontend/user/utils';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useSelectSendersAndReceivers from './select-senders-and-receivers.controller';

const ID = 'select-senders-and-receivers';

// eslint-disable-next-line no-unused-vars
type IndexedAction = (...args: [number]) => void;
// eslint-disable-next-line no-unused-vars
type UpdateSelectedUser = (...args: [number, string | null]) => void;

interface UserSelectionSectionProps {
    id: string;
    title: ReactNode;
    selectedUsers: (string | null)[];
    subsets: (string[] | undefined)[];
    canAdd: boolean;
    addLabel: ReactNode;
    removeLabel: string;
    fetchOptions: ReturnType<typeof useSelectSendersAndReceivers>['fetchSenderOptions'];
    onAdd(): void;
    onRemove: IndexedAction;
    onUpdate: UpdateSelectedUser;
}

const UserSelectionSection = ({
    id,
    title,
    selectedUsers,
    subsets,
    canAdd,
    addLabel,
    removeLabel,
    fetchOptions,
    onAdd,
    onRemove,
    onUpdate
}: UserSelectionSectionProps) => (
    <section className='flex w-full flex-col gap-3'>
        <h2 className='text-foreground-default text-sm font-semibold'>{title}</h2>

        {selectedUsers.map((selectedUser, index) => (
            <div
                key={index + '-' + (selectedUser ? selectedUser : '')}
                className='flex w-full items-center gap-3'>
                <div className='min-w-0 flex-1'>
                    <UserSelectV2
                        id={`${ID}-${id}-${index}`}
                        hideLabel
                        value={selectedUser}
                        onChange={(user) => onUpdate(index, user)}
                        isClearable
                        minSearchCharacters={0}
                        customLabel={(user) => new User(user).getDisplayName()}
                        fetchOverwrite={fetchOptions}
                        subset={subsets[index]}
                    />
                </div>

                {selectedUsers.length > 1 && (
                    <button
                        onClick={() => onRemove(index)}
                        className='text-foreground-default hover:text-foreground-emphasis flex size-11 shrink-0 items-center justify-center transition-colors'
                        aria-label={removeLabel}>
                        <ICONS.BUTTON.DELETE className='size-5' />
                    </button>
                )}
            </div>
        ))}

        {canAdd && (
            <button
                onClick={onAdd}
                className='text-foreground-default flex w-full items-center justify-end gap-3 text-base leading-normal font-medium'>
                <Plus className='size-3.5' />
                <span>{addLabel}</span>
            </button>
        )}
    </section>
);

const SelectSendersAndReceivers = () => {
    const intl = useIntl();
    const viewProps = useSelectSendersAndReceivers();
    const removeSenderLabel = intl.formatMessage({
        id: 'kiosk.wizard.select-senders-and-receivers.remove-sender',
        defaultMessage: 'Remove sender',
        description: 'Accessible label for removing a sender from the kiosk sender and receiver selection screen'
    });
    const removeReceiverLabel = intl.formatMessage({
        id: 'kiosk.wizard.select-senders-and-receivers.remove-receiver',
        defaultMessage: 'Remove receiver',
        description: 'Accessible label for removing a receiver from the kiosk sender and receiver selection screen'
    });

    return (
        <WizardScreenContainer
            id={ID}
            header={{ title: CommonMessage.BUTTONS.DROP_OFF }}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.onSubmit,
                submitDisabled: !viewProps.canContinue || viewProps.isLoading,
                submitLabel: CommonMessage.BUTTONS.CONTINUE
            }}
            containerClassName='relative flex h-full w-full flex-col content-stretch items-start gap-6 p-6'>
            <p className='text-foreground-default relative w-full shrink-0 text-base leading-normal font-medium'>
                <FormattedMessage
                    id='kiosk.wizard.select-senders-and-receivers.instructions'
                    defaultMessage='Select the sender and receiver for this drop-off.'
                    description='Instructions for selecting sender and receiver when QR code email lookup has ambiguous matches'
                />
            </p>

            {viewProps.isLoading ? (
                <Spinner />
            ) : (
                <div className='flex w-full flex-col gap-6'>
                    <UserSelectionSection
                        id='sender'
                        title={CommonMessage.OBJECTS.TRANSACTION.SENDERS}
                        selectedUsers={viewProps.selectedSenders}
                        subsets={viewProps.senderSubsets}
                        canAdd={viewProps.canAddSender}
                        fetchOptions={viewProps.fetchSenderOptions}
                        addLabel={
                            <FormattedMessage
                                id='kiosk.wizard.select-senders-and-receivers.add-sender'
                                defaultMessage='Add another sender'
                                description='Button to add another sender on the kiosk sender and receiver selection screen'
                            />
                        }
                        removeLabel={removeSenderLabel}
                        onAdd={viewProps.addSender}
                        onRemove={viewProps.removeSender}
                        onUpdate={viewProps.updateSender}
                    />

                    <UserSelectionSection
                        id='receiver'
                        title={CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}
                        selectedUsers={viewProps.selectedReceivers}
                        subsets={viewProps.receiverSubsets}
                        canAdd={viewProps.canAddReceiver}
                        fetchOptions={viewProps.fetchReceiverOptions}
                        addLabel={
                            <FormattedMessage
                                id='kiosk.wizard.select-senders-and-receivers.add-receiver'
                                defaultMessage='Add another receiver'
                                description='Button to add another receiver on the kiosk sender and receiver selection screen'
                            />
                        }
                        removeLabel={removeReceiverLabel}
                        onAdd={viewProps.addReceiver}
                        onRemove={viewProps.removeReceiver}
                        onUpdate={viewProps.updateReceiver}
                    />
                </div>
            )}
        </WizardScreenContainer>
    );
};

export default SelectSendersAndReceivers;
